import { Close } from "@mui/icons-material";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import clsx from "clsx";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import APIIcon from "../../../assets/images/icons/APIIcon.tsx";
import DeviceIcon from "../../../assets/images/icons/DeviceIcon.tsx";
import HuggingFace from "../../../assets/images/icons/HuggingFace.tsx";
import ResearchPaperIcon from "../../../assets/images/icons/ResearchPaperIcon.tsx";
import BriaTooltip from "../../../components/common/BriaTooltip/BriaTooltip.tsx";
import LinkConstants from "../../../constants/LinkConstants.ts";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents.ts";
import RouterConstants from "../../../constants/RouterConstants.ts";
import usePostHog from "../../../hooks/usePostHog.tsx";
import { QueryParamKeys } from "../../../services/QueryParamsService.ts";
import { hasOrganization } from "../../../utils/index.ts";
import styles from "./LearnMorePopup.module.scss";
interface IProps {
	open: boolean;
	onClose: () => void;
}

type Resource = {
	disabled?: boolean;
	title: string;
	link: string;
	icon: JSX.Element;
};

export default function LearnMorePopup({ open, onClose }: IProps) {
	const { t } = useTranslation("fibo", { keyPrefix: "lab.header.learnMore" });
	const { t: tLab } = useTranslation("fibo", { keyPrefix: "lab" });
	const { trackEvent } = usePostHog();

	// Track popup opened
	useEffect(() => {
		if (open) {
			trackEvent(POSTHOG_EVENTS.FIBO_LEARN_MORE_POPUP_OPENED);
		}
	}, [open]);

	const handleOnClose = useCallback(() => {
		trackEvent(POSTHOG_EVENTS.FIBO_LEARN_MORE_POPUP_CLOSED);
		onClose();
	}, [onClose, trackEvent]);

	const handleResourceClick = useCallback(
		(resource: Resource) => {
			trackEvent(POSTHOG_EVENTS.FIBO_LEARN_MORE_RESOURCE_CLICKED, {
				resourceTitle: resource.title,
				resourceLink: resource.link,
			});
		},
		[trackEvent],
	);

	// Build resources once per locale change
	const resources: Resource[] = useMemo(
		() => [
			{
				title: t("resources.buttons.modelCard"),
				link: LinkConstants.MODELS.FOUNDATION.FIBO.HUGGING_FACE.MODEL,
				icon: <HuggingFace />,
			},
			{
				title: t("resources.buttons.researchPaper"),
				link: LinkConstants.BRIA_FIBO_RESEARCH_PAPER,
				icon: <ResearchPaperIcon />,
			},
			{
				title: t("resources.buttons.viewApiDocs"),
				link: LinkConstants.IMAGE_GENERATION.FIBO.DOCS,
				icon: <APIIcon />,
			},
			{
				title: t("resources.buttons.briaPlatform"),
				link: `${RouterConstants.CONSOLE.fullPath}${!hasOrganization() ? `?${QueryParamKeys.CREATE_ORG}=true` : ""}`,
				icon: <DeviceIcon />,
			},
		],
		[t],
	);

	// Compact config for the two top features
	const topFeatures = useMemo(
		() => [
			{
				title: t("builtForBuilders.title"),
				description: t("builtForBuilders.description"),
			},
			{
				title: t("designedForProfessionals.title"),
				description: t("designedForProfessionals.description"),
			},
		],
		[t],
	);

	// Keys for the architecture capabilities list – avoids repeating similar blocks
	const archFeatureKeys = useMemo(
		() =>
			[
				"longStructuredCaptions",
				"humanMachineWritable",
				"disentangledRepresentation",
				"llmPoweredBridge",
				"responsibleByDesign",
			] as const,
		[],
	);

	return (
		<Dialog classes={{ paper: styles.paper }} onClose={handleOnClose} open={open} maxWidth={false}>
			<Box className={styles.container}>
				<IconButton onClick={handleOnClose} className={styles.closeButton}>
					<Close />
				</IconButton>

				<Box className={styles.content}>
					<Box className={styles.headerContent}>
						<Typography className={styles.title}>{t("title")}</Typography>
						<Typography className={styles.subtitle}>{t("subtitle")}</Typography>
						<Typography className={styles.description}>{t("description")}</Typography>

						<Box className={styles.resources}>
							<Typography className={styles.resourcesTitle}>{t("resources.title")}</Typography>
							<Box className={styles.resourcesButtons}>
								{resources.map((resource) =>
									resource.disabled ? (
										<BriaTooltip title={tLab("comingSoon")} key={resource.link}>
											<Box className={clsx(styles.resourcesButton, styles.disabled)}>
												{resource.icon}
												{resource.title}
											</Box>
										</BriaTooltip>
									) : (
										<Link
											className={styles.resourcesButton}
											key={resource.link}
											to={resource.link}
											target="_blank"
											onClick={() => handleResourceClick(resource)}
										>
											{resource.icon}
											{resource.title}
										</Link>
									),
								)}
							</Box>
						</Box>
					</Box>

					<Box className={styles.features}>
						<Box className={styles.featuresContainer}>
							{topFeatures.map((f) => (
								<Box className={styles.feature} key={f.title}>
									<Typography className={styles.featureTitle}>{f.title}</Typography>
									<Typography className={styles.featureDescription}>{f.description}</Typography>
								</Box>
							))}
						</Box>

						<Box className={styles.feature}>
							<Typography className={styles.featureTitle}>
								{t("architectureCapabilities.title")}
							</Typography>

							<ul className={styles.featuresItemsList}>
								{archFeatureKeys.map((k) => (
									<li className={styles.featureItem} key={k}>
										<Typography className={styles.itemTitle}>
											{t(`architectureCapabilities.features.${k}.title`)}
										</Typography>
										<Typography className={styles.itemDescription}>
											{t(`architectureCapabilities.features.${k}.description`)}
										</Typography>
									</li>
								))}
							</ul>
						</Box>
					</Box>
				</Box>
			</Box>
		</Dialog>
	);
}
